/**
 * The Lines tab. An estimate is a proposed order: a header and its lines.
 * Today every Tharstern estimate has one line — a multi-part estimate is
 * still one line, because its manufacturing parts sit inside the line's spec
 * and are never lines.
 *
 * The table reads `# · What · Qty · Price · Status`; the row's ▸ holds the
 * spec and the line's price. The estimate's own reference facts are on
 * Details.
 *
 * A merchandise request has no such table (Matt, 15 Oct 2026). Its lines are
 * the Promo Office quote's, listed with their quantities and prices in "What
 * was asked for"; a single generic row beside them had no quantity, an empty
 * ▸ and only repeated the price.
 */
import { useId, useState, type ReactNode } from "react";
import type {
  EstimateDetailViewItem,
  EstimatePart,
} from "@shared-core/data/api-types";
import type { MerchandiseDraftEstimate } from "@shared-core/data/merchandise-types";
import {
  partDescriptors,
  specificationCoverageExplanation,
} from "@shared-core/domain/estimate-presentation";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Badge } from "../../app/components/primitives/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { InfoTip } from "../../app/components/patterns/InfoTip";
import { DraftEstimateCard } from "../../app/components/domain/merchandise/DraftEstimateCard";
import { RowExpander } from "../../app/components/patterns/RowExpander";
import { Gap, KeyValues, Section, SubHeading, TabContainer } from "./atoms";

type LineStatusTone = "success" | "neutral" | "warning";

/** The line's status pill: words and tone, from a state the estimate records. */
export interface LineStatus {
  label: string;
  tone: LineStatusTone;
}

/**
 * The line's status, derived only from what the estimate records — or null
 * when nothing truthful can be said:
 * - an estimate Tharstern has not replicated yet: Pending sync;
 * - Quote expired only when its validity has run out and it was not won;
 * - Priced when the line carries a price.
 */
export function lineStatus(
  estimate: EstimateDetailViewItem,
  pricePence: number | null,
): LineStatus | null {
  if (estimate.information.coverage === "pending_sync") {
    return { label: "Pending sync", tone: "neutral" };
  }
  if (estimate.information.expiry?.is_expired && estimate.status !== "won") {
    return { label: "Quote expired", tone: "warning" };
  }
  if (pricePence != null) return { label: "Priced", tone: "success" };
  return null;
}

export interface EstimateLine {
  /** What it is: the spec summary, the product, or the description. */
  what: string;
  quantity: number | null;
  pricePence: number | null;
  /** Null when the estimate records nothing truthful to say. */
  status: LineStatus | null;
}

/** The line, put together from what the estimate already says. */
function estimateLine(estimate: EstimateDetailViewItem): EstimateLine {
  const { specification, financials, information } = estimate;
  const pricePence = financials.quoted_total_pence ?? null;
  return {
    what:
      (specification.summary ||
        specification.product?.product_type_name ||
        information.product_type_name ||
        estimate.description) ??
      "",
    quantity: specification.quantity ?? estimate.quantity ?? null,
    pricePence,
    status: lineStatus(estimate, pricePence),
  };
}

export function LinesTab({
  estimate,
  draft,
  whatWasAskedFor,
}: {
  estimate: EstimateDetailViewItem;
  draft: MerchandiseDraftEstimate | null;
  /** A draft's request, ticket and quote, with Unlink quote: the top of Lines. */
  whatWasAskedFor: ReactNode;
}) {
  return (
    <TabContainer label="Lines">
      {whatWasAskedFor}
      {!draft && <EstimateLines estimate={estimate} />}
    </TabContainer>
  );
}

/** A Tharstern estimate's one line, with its spec and parts under the ▸. */
function EstimateLines({ estimate }: { estimate: EstimateDetailViewItem }) {
  const line = estimateLine(estimate);
  const detailId = useId();
  const [isOpen, setOpen] = useState(false);
  const totalPence = line.pricePence;
  const showDescriptionOnTop =
    !estimate.specification.summary && !!estimate.description;
  return (
    <>
      {showDescriptionOnTop && (
        <Section label="Description">
          <p className="max-w-3xl text-sm">{estimate.description}</p>
        </Section>
      )}
      <Section title="Lines" label="Estimate lines">
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8">
                <span className="sr-only">Detail</span>
              </TableHead>
              <TableHead className="w-10">#</TableHead>
              <TableHead>What</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="align-top">
                <RowExpander
                  label="Line 1 detail"
                  isOpen={isOpen}
                  onToggle={() => setOpen(!isOpen)}
                  controls={detailId}
                />
              </TableCell>
              <TableCell className="align-top tabular-nums text-muted-foreground">
                1
              </TableCell>
              <TableCell className="max-w-md whitespace-normal align-top font-medium">
                {line.what || <Gap>Not described</Gap>}
              </TableCell>
              <TableCell className="text-right tabular-nums align-top">
                {line.quantity?.toLocaleString("en-GB")}
              </TableCell>
              <TableCell className="text-right tabular-nums align-top">
                {line.pricePence != null && formatQuotedPrice(line.pricePence)}
              </TableCell>
              <TableCell className="align-top">
                {line.status && (
                  <Badge variant={line.status.tone}>{line.status.label}</Badge>
                )}
              </TableCell>
            </TableRow>
            {isOpen && (
              <TableRow className="hover:bg-transparent">
                <TableCell />
                <TableCell colSpan={5} className="whitespace-normal pb-5">
                  <div id={detailId} className="space-y-5">
                    <LineSpec
                      estimate={estimate}
                      descriptionOnTop={showDescriptionOnTop}
                    />
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {totalPence != null && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={4} className="font-medium">
                  Total (ex VAT)
                </TableCell>
                <TableCell className="text-right tabular-nums font-semibold">
                  {formatQuotedPrice(totalPence)}
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </Section>
    </>
  );
}

/** The line's ▸: its description, spec, parts and its price. */
function LineSpec({
  estimate,
  descriptionOnTop,
}: {
  estimate: EstimateDetailViewItem;
  /** The description is shown above the table, so not again here. */
  descriptionOnTop: boolean;
}) {
  const { information, specification, financials, origin } = estimate;
  const coverage = specificationCoverageExplanation(specification.coverage);
  const linePence = financials.quoted_total_pence ?? null;
  return (
    <>
      {!descriptionOnTop && (
        <section aria-label="Description" className="space-y-1">
          {estimate.description ? (
            <p className="text-sm">{estimate.description}</p>
          ) : (
            <p className="text-sm">
              <Gap>No description was typed on this estimate</Gap>
            </p>
          )}
          {specification.summary &&
            specification.summary !== estimate.description && (
              <p className="text-sm text-muted-foreground">
                {specification.summary}
              </p>
            )}
        </section>
      )}
      <section aria-label="Specification" className="space-y-2">
        <SubHeading>
          Specification
          {coverage && (
            <InfoTip label="About the specification" className="ml-1">
              {coverage}
            </InfoTip>
          )}
        </SubHeading>
        <KeyValues
          rows={[
            {
              label: "Customer's product reference",
              value: information.customer_product_ref,
            },
            {
              label: "Product",
              value:
                specification.product?.product_type_name ??
                information.product_type_name ??
                origin.product_type_name,
            },
            {
              label: "Quantity",
              value: specification.quantity?.toLocaleString("en-GB"),
            },
          ]}
        />
      </section>
      <section aria-label="Parts" className="space-y-2">
        <SubHeading>Parts</SubHeading>
        <PartsList
          parts={specification.parts ?? []}
          coverage={specification.coverage}
        />
      </section>
      {linePence != null && (
        <section aria-label="Line price" className="space-y-2">
          <SubHeading>Price</SubHeading>
          <KeyValues
            rows={[
              {
                label: "Line price",
                value: (
                  <span className="tabular-nums">
                    {formatQuotedPrice(linePence)}
                  </span>
                ),
              },
              {
                label: "Each",
                value:
                  estimate.quantity > 0 ? (
                    <span className="tabular-nums">
                      {formatQuotedPrice(linePence / estimate.quantity)}
                    </span>
                  ) : null,
              },
            ]}
          />
        </section>
      )}
    </>
  );
}

// ── The spec's parts ───────────────────────────────────────────────────────

function PartsList({
  parts,
  coverage,
}: {
  parts: EstimatePart[];
  coverage: EstimateDetailViewItem["specification"]["coverage"];
}) {
  if (parts.length === 0) {
    return (
      <p className="text-sm">
        <Gap>
          {coverage === "parts_pending_sync"
            ? "Parts have not arrived from Tharstern yet"
            : "No parts recorded on this estimate"}
        </Gap>
      </p>
    );
  }
  return (
    <ul className="divide-y divide-border">
      {parts.map((part, index) => {
        const descriptors = partDescriptors(part);
        return (
          <li
            key={part.unique_part_number ?? part.line_number ?? index}
            className="py-2 first:pt-0"
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium">
                {part.part_name ?? `Part ${part.line_number ?? index + 1}`}
              </span>
              {part.line_number != null && (
                <span className="text-xs text-muted-foreground">
                  Part line {part.line_number}
                </span>
              )}
            </div>
            {descriptors.length > 0 ? (
              <p className="text-sm text-muted-foreground">
                {descriptors.join(" · ")}
              </p>
            ) : (
              <p className="text-sm">
                <Gap>No paper, size or extent recorded</Gap>
              </p>
            )}
            {part.finishing && part.finishing.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {part.finishing.map((process, processIndex) => (
                  <Badge
                    key={process.process_type_id ?? processIndex}
                    variant="outline"
                  >
                    {process.name ?? "Unnamed process"}
                  </Badge>
                ))}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** "What was asked for": a draft's request, ticket and quote, at the top of Lines. */
export function WhatWasAskedFor({
  draft,
  description,
  unlink,
}: {
  draft: MerchandiseDraftEstimate;
  description: string | null;
  unlink: ReactNode;
}) {
  return (
    <Section title="What was asked for" label="What was asked for">
      {description && <p className="max-w-3xl text-sm">{description}</p>}
      <DraftEstimateCard request={draft} />
      {unlink}
    </Section>
  );
}
